// 인증 흐름 API 호출 모음.
// 라우트 핸들러는 docs/api-spec.md §4.1 의 엔드포인트를 본 모듈을 통해서만 호출한다.
// React Query mutation 으로 감쌀지 단순 호출로 둘지는 사용처마다 결정한다 (현재는 단순 fetch 진행).

import { api } from "@/api/client.ts";

// /auth/sms/request 응답.
// dev 환경(로컬/원격) 에서만 devCode 가 포함된다 (운영에서는 절대 포함되지 않음).
export interface SendOtpResponse {
  ok: true;
  expiresInSec: number;
  cooldownSec: number;
  devCode?: string;
}

// /auth/sms/verify 응답.
// verificationToken 은 단기(15분 미만) JWT 로, 약관 동의/회원가입 완료 시 백엔드가 다시 검증한다.
export interface VerifyOtpResponse {
  ok: true;
  verificationToken: string;
  expiresAtSec: number;
}

// SMS 인증번호 발송 요청.
// withAuth: false 로 둬 토큰이 있어도 Authorization 헤더를 붙이지 않는다 (게스트 흐름).
export function sendOtp(phone: string): Promise<SendOtpResponse> {
  return api.post<SendOtpResponse>("/auth/sms/request", { phone }, { withAuth: false });
}

// 인증번호 검증.
export function verifyOtp(phone: string, code: string): Promise<VerifyOtpResponse> {
  return api.post<VerifyOtpResponse>("/auth/sms/verify", { phone, code }, { withAuth: false });
}

// 소셜 로그인 시작 URL 을 만든다.
// 웹은 window.location.href 대입으로 풀 페이지 리다이렉트, 네이티브는 인앱 브라우저로 연다.
//  - native=true 면 서버가 콜백에서 앱으로 돌아올 딥링크(openchuksan://)로 302 하도록 client=native 를 붙인다.
export function buildSocialStartUrl(
  provider: "kakao" | "naver" | "google",
  native = false,
): string {
  const base = import.meta.env.VITE_API_BASE;
  const suffix = native ? "?client=native" : "";
  return `${base}/auth/social/${provider}/start${suffix}`;
}

// 회원가입/로그인 약관 동의 입력 형식. 백엔드 zod 스키마와 1:1 매핑.
export interface SignupConsentsInput {
  terms: boolean;
  privacy: boolean;
  over14: boolean;
  marketingPush: boolean;
  marketingSms: boolean;
}

export interface SignupOrLoginPhoneRequest {
  source: "phone";
  verificationToken: string;
  displayName?: string;
  consents: SignupConsentsInput;
}

// 소셜 가입/로그인 요청.
// linkToken(소셜 콜백 발급) + verificationToken(휴대폰 OTP) 둘 다 필요하다(정책: 소셜 후 휴대폰 인증 필수).
// provider 는 linkToken 에 이미 담겨 서버가 도출하므로 본 요청에는 보내지 않는다.
export interface SignupOrLoginSocialRequest {
  source: "social";
  linkToken: string;
  verificationToken: string;
  // OTP 단계에서 입력한 이름. 소셜 닉네임보다 우선해 회원 이름으로 저장된다.
  displayName?: string;
  consents: SignupConsentsInput;
}

export type SignupOrLoginRequest = SignupOrLoginPhoneRequest | SignupOrLoginSocialRequest;

export interface SignupOrLoginResponse {
  ok: true;
  accessToken: string;
  expiresAtSec: number;
  refreshToken: string;
  refreshExpiresAt: string;
  user: {
    id: string;
    displayName: string | null;
    tier: string;
  };
  newlyCreated: boolean;
}

// 약관 동의 후 회원 조회/생성 + 세션 발급.
// 응답으로 받은 accessToken/refreshToken/user 를 authStore.setSession 에 저장한다.
export function signupOrLogin(input: SignupOrLoginRequest): Promise<SignupOrLoginResponse> {
  return api.post<SignupOrLoginResponse>("/auth/signup-or-login", input, { withAuth: false });
}

// 재로그인 판별 입력. 소셜은 linkToken, 휴대폰은 verificationToken(둘 다 있으면 계정 통합 판단).
export interface ResolveLoginInput {
  verificationToken?: string;
  linkToken?: string;
}

// 기존 회원이면 세션을, 신규면 signup_required 를 돌려준다.
export type ResolveLoginResponse =
  | ({ ok: true; status: "login" } & Omit<SignupOrLoginResponse, "ok" | "newlyCreated">)
  | { ok: true; status: "signup_required" };

// 약관/추가단계 전에 기존 회원 여부를 판별한다.
//  - status "login": 기존 회원 -> 세션 발급됨(약관/OTP 추가 없이 바로 로그인).
//  - status "signup_required": 신규 -> 호출부가 약관 동의 화면으로 진행해야 한다.
export function resolveLogin(input: ResolveLoginInput): Promise<ResolveLoginResponse> {
  return api.post<ResolveLoginResponse>("/auth/login", input, { withAuth: false });
}

export interface MeResponse {
  ok: true;
  user: {
    id: string;
    phone: string;
    displayName: string | null;
    tier: string;
    pointsBalance: number;
  };
}

// 본인 정보 조회. 토큰 만료 시 401 -> 자동 refresh 인터셉터(1-G 라운드 2) 에서 처리한다.
export function fetchMe(): Promise<MeResponse> {
  return api.get<MeResponse>("/auth/me");
}

export interface RefreshResponse {
  ok: true;
  accessToken: string;
  expiresAtSec: number;
}

// 리프레시 토큰으로 새 액세스 발급. 응답의 accessToken 으로 authStore 갱신은 호출자가 수행한다.
export function refreshSession(refreshToken: string): Promise<RefreshResponse> {
  return api.post<RefreshResponse>("/auth/refresh", { refreshToken }, { withAuth: false });
}

// 단일 디바이스 로그아웃. 서버는 해당 리프레시 토큰만 무효화한다 (다른 디바이스 세션은 살아 있다).
export function logoutSession(refreshToken: string): Promise<{ ok: true }> {
  return api.post<{ ok: true }>("/auth/logout", { refreshToken }, { withAuth: false });
}

// 회원 탈퇴. PII 마스킹 + soft delete + 전체 세션 폐기. 호출 후 클라이언트는 세션을 비운다.
export function withdrawAccount(): Promise<{ ok: true }> {
  return api.delete<{ ok: true }>("/auth/me");
}
